import {
  type AqiStandardDto,
  type Brand,
  type DatasourceId,
  DatasourceMeasurementsDto,
  MetricIndex,
  Pollutant,
  PollutantConfigDto,
} from '../types';
import { findAqiLevel, maybeConvertAqcToAqi } from './aqi';
import sortBy from 'lodash/sortBy';

export type SeriesLabel = Brand<string, 'SeriesLabel'>;

class MeasurementsTrend {
  trendValue: number;
  trendStatus: 'rising' | 'falling' | 'flat' | 'no-recent-data' | 'data-gap-too-wide' | 'invalid';
  comparisonGapHours: number;
}

export class MeasurementSeries {
  series: (number | undefined)[];
  colors: string[];
  trend: MeasurementsTrend;
  seriesLabel: SeriesLabel;
  pollutant: Pollutant;
}

export class ProcessedMeasurements {
  constructor(
    public times: string[],
    public datasourceId: DatasourceId,
    private _values: Record<string, MeasurementSeries>
  ) {}

  getSeriesByLabel(seriesLabel: SeriesLabel): MeasurementSeries {
    return this._values[seriesLabel];
  }

  getSeriesByPollutant(pollutant: Pollutant): MeasurementSeries {
    return Object.values(this._values).find((v) => v.pollutant === pollutant)!;
  }

  isAvailable(label: SeriesLabel): boolean {
    return this._values[label]?.series.some((y) => y !== null && y !== undefined) ?? false;
  }

  get seriesLabels(): SeriesLabel[] {
    // sorted alphabetically
    return sortBy(Object.values(this._values), ({ seriesLabel }) => seriesLabel).map((v) => v.seriesLabel);
  }
}

function calcTrend(
  measurements: (number | undefined)[],
  maxGapHours = 2,
  maxDelayHours = 2,
  lookbackHours = 4
): MeasurementsTrend {
  measurements = measurements.slice(-lookbackHours);
  const filteredMeasurements = measurements
    .map((value, index) => [value, index])
    .filter(([m]) => m !== undefined && m !== null) as [[number, number]];
  if (filteredMeasurements.length < 2) {
    // Not enough data to calculate the trend.
    return {
      trendValue: 0,
      trendStatus: 'no-recent-data',
      comparisonGapHours: 0,
    };
  }

  const [firstValue, firstIndex] = filteredMeasurements.at(-2)!;
  const [secondValue, secondIndex] = filteredMeasurements.at(-1)!;

  if (measurements.length - secondIndex > maxDelayHours) {
    // Data is too delayed to calculate the trend.
    return {
      trendValue: 0,
      trendStatus: 'no-recent-data',
      comparisonGapHours: 0,
    };
  }

  const comparisonGapHours = secondIndex - firstIndex;
  if (comparisonGapHours > maxGapHours) {
    // If the measurements are more than `maxGapHours` hours apart, we don't calculate the trend.
    return {
      trendValue: 0,
      trendStatus: 'data-gap-too-wide',
      comparisonGapHours,
    };
  }

  const trendValue = Number((((secondValue - firstValue) / firstValue) * 100).toFixed(0));
  if (isNaN(trendValue) || !isFinite(trendValue) || (firstValue <= 1 && secondValue <= 1 && trendValue != 0)) {
    return {
      trendValue: 0,
      trendStatus: 'invalid',
      comparisonGapHours: 0,
    };
  }

  const trendDirection = trendValue > 0 ? 'rising' : trendValue < 0 ? 'falling' : 'flat';
  return {
    trendValue,
    trendStatus: trendDirection,
    comparisonGapHours,
  };
}

export function processMeasurements(
  measurements: DatasourceMeasurementsDto,
  pollutantConfigs: PollutantConfigDto[],
  aqiStandard: AqiStandardDto
): ProcessedMeasurements {
  const aqiInfos = pollutantConfigs.map(({ metricInfos, pollutant, displayName }) => {
    return {
      aqiKey: metricInfos.find((info) => info.indexType === MetricIndex.Aqi)!.key,
      pollutant,
      displayName,
    };
  });

  const seriesArray: MeasurementSeries[] = aqiInfos.map(({ aqiKey, pollutant, displayName }) => {
    const series = (measurements.measurements[aqiKey] ?? []).map((conc) =>
      maybeConvertAqcToAqi(aqiStandard, pollutant, conc)
    );
    return {
      series,
      colors: series.map((y) => findAqiLevel(aqiStandard, y).color),
      trend: calcTrend(series),
      pollutant,
      displayName,
      seriesLabel: `${displayName} ${aqiStandard.name}` as SeriesLabel,
    };
  });

  const procMeasurements = new ProcessedMeasurements(
    measurements.times as unknown[] as string[],
    measurements.datasourceId,
    Object.fromEntries(seriesArray.map((v) => [v.seriesLabel, v]))
  );

  return procMeasurements;
}
